import React from 'react';

import { EffectiveMarginAvailability, EffectiveMarginDecimal, EffectiveMarginTotals } from '@/types/effectiveMargin';
import { formatAvailableCurrency } from './formatters';


interface SummaryMetricProps {
  label: string;
  value: EffectiveMarginDecimal;
  availability: EffectiveMarginAvailability;
  note: string;
  tooltip: string;
  valueClassName?: string;
}

const SummaryMetric: React.FC<SummaryMetricProps> = ({
  label,
  value,
  availability,
  note,
  tooltip,
  valueClassName = '',
}) => (
  <div className="border border-black/10 bg-white px-4 py-3.5 min-h-[112px]">
    <div className="flex items-center gap-1.5 text-[9.5px] font-bold tracking-[0.13em] uppercase text-[#555]">
      <span>{label}</span>
      <span className="group relative inline-flex" tabIndex={0} aria-label={`${label}: ${tooltip}`}>
        <span className="inline-grid h-4 w-4 place-items-center rounded-full border border-black/30 text-[10px] font-semibold normal-case tracking-normal text-[#555]">?</span>
        <span role="tooltip" className="pointer-events-none invisible absolute left-1/2 top-6 z-20 w-64 -translate-x-1/2 border border-black/15 bg-[#171717] px-3 py-2.5 text-[11px] font-normal normal-case leading-relaxed tracking-normal text-white opacity-0 shadow-lg transition-opacity group-hover:visible group-hover:opacity-100 group-focus:visible group-focus:opacity-100">
          {tooltip}
        </span>
      </span>
    </div>
    <div className={`mt-1.5 font-serif text-[25px] leading-none tracking-[-0.01em] ${valueClassName}`}>
      {formatAvailableCurrency(value, availability)}
    </div>
    <div className="mt-2 text-[10.5px] text-[#666]">{note}</div>
  </div>
);

interface EffectiveMarginSummaryProps {
  totals: EffectiveMarginTotals;
}

const EffectiveMarginSummary: React.FC<EffectiveMarginSummaryProps> = ({ totals }) => (
  <div
    role="region"
    aria-label="Effective margin summary"
    className="grid grid-cols-3 gap-3 max-[900px]:grid-cols-1"
  >
    <SummaryMetric
      label="Pershing baseline"
      value={totals.raw_pershing_house_requirement}
      availability={totals.raw_pershing_house_requirement_availability}
      note="Raw position-level requirement"
      tooltip="The sum of the available position-level Pershing house requirements received from the source snapshot. Missing requirements remain unavailable, not zero."
    />
    <SummaryMetric
      label="Effective Margin"
      value={totals.effective_margin}
      availability={totals.effective_margin_availability}
      note="Conservative fallback treatment"
      tooltip="The portfolio requirement after retaining every available Pershing house requirement in the fallback treatment."
      valueClassName="text-[#E50025]"
    />
    <SummaryMetric
      label="Recognised Reduction"
      value={totals.recognised_reduction}
      availability={totals.recognised_reduction_availability}
      note="No reduction recognised"
      tooltip="The reported difference between allocated input Pershing requirements and Effective Margin. No reduction is recognised for the fallback treatment."
      valueClassName="text-[#1f6f43]"
    />
  </div>
);

export default EffectiveMarginSummary;
